import { useParams, Link } from "react-router";
import { useShopProducts, useCategoryEnrichment } from "@/lib/api.ts";
import { ProductCard } from "@/components/ProductCard.tsx";
import { fromUrlFriendly } from "@/lib/utils.ts";
import { useDocumentHead } from "@/hooks/useDocumentHead.ts";

export function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const { data, isLoading } = useShopProducts();
  // AI-generated buyer's guide (scripts/enrich-collections.mjs). 4 sections
  // of category-level analytical content. Returns empty if not yet generated.
  const { data: enrichment } = useCategoryEnrichment(category ?? "");
  const enrichedSections = enrichment?.sections ?? [];

  const displayName = fromUrlFriendly(category ?? "");

  useDocumentHead({
    title: displayName,
    description: `Shop the best ${displayName.toLowerCase()} products with expert reviews and exclusive deals at The Secret Trends.`,
  });
  const allProducts = data ?? {};

  const matchedKey = Object.keys(allProducts).find(
    (k) => k.toLowerCase() === displayName.toLowerCase()
  );
  const products = matchedKey ? allProducts[matchedKey] : [];

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-heading border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1360px] px-6 py-12 lg:px-10 lg:py-16">
      {/* Breadcrumb */}
      <nav className="mb-8 flex items-center gap-2 text-xs text-text-muted">
        <Link to="/" className="hover:text-heading">
          Home
        </Link>
        <span>/</span>
        <span className="text-heading">{displayName}</span>
      </nav>

      <div className="mb-10 flex items-baseline gap-3">
        <h1 className="text-[32px] font-medium tracking-tight text-heading">
          {displayName}
        </h1>
        <span className="text-sm text-text-muted">
          ({products.length} {products.length === 1 ? "product" : "products"})
        </span>
      </div>

      {products.length > 0 ? (
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4 lg:gap-8">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      ) : (
        <p className="py-24 text-center text-sm text-text-muted">
          No products found in this category.
        </p>
      )}

      {/* Buyer's guide — depth content placed BELOW the product grid so
          quick-purchase users hit products first, but searchers and scrollers
          find genuine analytical content. */}
      {enrichedSections.length > 0 && (
        <div className="mt-20 border-t border-border pt-12">
          <h2 className="mb-8 text-2xl font-medium tracking-tight text-heading">
            {displayName} buyer's guide
          </h2>
          <div className="space-y-10 max-w-prose">
            {enrichedSections.map((s, i) => (
              <div key={i}>
                <h3 className="mb-3 text-lg font-medium text-heading">
                  {s.heading}
                </h3>
                <p className="text-[15px] leading-[1.7] text-text-muted">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
